import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Button, Dialog,
  DialogTitle, DialogContent, DialogActions,
  TextField, Table, TableBody, TableCell, TableHead,
  TableRow, Paper, IconButton, MenuItem, Grid, Chip,
  Tooltip, FormControlLabel, Checkbox, Divider, Alert,
  CardHeader, InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Checklist as ChecklistIcon,
  ContentCopy as ContentCopyIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import {
  getChecklists, createChecklist, updateChecklist, deleteChecklist
} from '../../services/api';

const defaultItem = () => ({ 
  key: '', 
  label: '', 
  type: 'boolean', 
  options: [], 
  required: false 
});

export const ChecklistMaster = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ page: 1, limit: 20, total: 0 });
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState({ title: '', description: '', items: [] });
  const [confirmDelete, setConfirmDelete] = useState({ open: false, id: null });

  const load = async (opts = {}) => {
    setLoading(true);
    try {
      const page = opts.page || meta.page || 1;
      const limit = opts.limit || meta.limit || 20;
      const q = opts.q !== undefined ? opts.q : search;
      const res = await getChecklists({ page, limit, q });
      const data = res.data;
      setItems(data.items || []);
      setMeta(prev => ({ 
        ...prev, 
        page: data.meta?.page || page, 
        limit: data.meta?.limit || limit, 
        total: data.meta?.total || 0 
      }));
    } catch (err) {
      enqueueSnackbar('Failed to load checklists', { variant: 'error' });
    } finally { 
      setLoading(false); 
    }
  };

  useEffect(() => { 
    load({ page: 1 }); 
  }, []);

  const handleOpenAdd = () => {
    setEditing(null);
    setForm({ title: '', description: '', items: [] });
    setOpen(true);
  };

  const handleEdit = (item) => {
    setEditing(item);
    setForm({ 
      title: item.title || '', 
      description: item.description || '', 
      items: (item.items || []).map(x => ({ ...x })) 
    });
    setOpen(true);
  };

  const handleSave = async () => {
    try {
      if (!form.title?.trim()) {
        enqueueSnackbar('Title is required', { variant: 'warning' });
        return;
      }
      
      // Validate items
      for (const item of form.items) {
        if (!item.label?.trim()) {
          enqueueSnackbar('All items must have a label', { variant: 'warning' });
          return;
        }
        if (!item.key?.trim()) {
          enqueueSnackbar('All items must have a key', { variant: 'warning' });
          return;
        }
      }

      if (editing) {
        await updateChecklist(editing._id, form);
        enqueueSnackbar('Checklist updated successfully', { variant: 'success' });
      } else {
        await createChecklist(form);
        enqueueSnackbar('Checklist created successfully', { variant: 'success' });
      }
      setOpen(false);
      load({ page: 1 });
    } catch (err) { 
      enqueueSnackbar('Save failed', { variant: 'error' }); 
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteChecklist(id);
      enqueueSnackbar('Checklist deleted successfully', { variant: 'success' });
      setConfirmDelete({ open: false, id: null });
      load({ page: 1 });
    } catch (err) { 
      enqueueSnackbar('Delete failed', { variant: 'error' }); 
    }
  };

  const addItemRow = () => setForm(prev => ({ 
    ...prev, 
    items: [...(prev.items || []), defaultItem()] 
  }));

  const removeItemRow = (idx) => setForm(prev => ({ 
    ...prev, 
    items: prev.items.filter((_, i) => i !== idx) 
  }));

  const updateItemRow = (idx, patch) => setForm(prev => ({ 
    ...prev, 
    items: prev.items.map((it, i) => i === idx ? { ...it, ...patch } : it) 
  }));

  const duplicateItem = (item) => {
    setEditing(null);
    setForm({
      title: `${item.title} (Copy)`,
      description: item.description,
      items: (item.items || []).map(x => ({ ...x }))
    });
    setOpen(true);
  };

  const totalPages = useMemo(() => 
    Math.max(1, Math.ceil(meta.total / meta.limit || 1)), 
    [meta.total, meta.limit]
  );

  return (
    <Box sx={{ p: 4, backgroundColor: 'grey.50', minHeight: '100vh' }}>
      {/* Header Section */}
      <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <ChecklistIcon sx={{ fontSize: 48, color: 'white' }} />
              <Box>
                <Typography 
                  variant="h3" 
                  fontWeight={700}
                  sx={{ color: 'white', fontSize: { xs: '2rem', md: '2.5rem' } }}
                >
                  Checklist Master
                </Typography>
                <Typography 
                  variant="h6" 
                  sx={{ color: 'white', opacity: 0.9, fontSize: '1.1rem' }}
                >
                  Manage and organize your checklists
                </Typography>
              </Box>
            </Box>
            
            <Button 
              variant="contained" 
              startIcon={<AddIcon />} 
              onClick={handleOpenAdd}
              sx={{
                backgroundColor: 'white',
                color: 'primary.main',
                fontSize: '1.1rem',
                fontWeight: 600,
                px: 3,
                py: 1.5,
                '&:hover': {
                  backgroundColor: 'grey.100',
                  transform: 'translateY(-2px)',
                  boxShadow: 3
                },
                transition: 'all 0.3s ease'
              }}
            >
              Add Checklist
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Search and Stats */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search checklists by title or description..."
                size="medium"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => { 
                  if (e.key === 'Enter') load({ page: 1, q: search }); 
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 28, color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiInputBase-input': {
                    fontSize: '1.1rem',
                    padding: '12px 14px'
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                <Chip 
                  icon={<ChecklistIcon />}
                  label={`${meta.total} Total Checklists`}
                  variant="outlined"
                  sx={{ fontSize: '1rem', py: 2 }}
                />
                <Chip 
                  label={`Page ${meta.page} of ${totalPages}`}
                  color="primary"
                  sx={{ fontSize: '1rem', py: 2 }}
                />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Checklists Table */}
      <Card sx={{ borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.08)' }}>
        <CardHeader
          title={
            <Typography variant="h5" fontWeight={600} sx={{ fontSize: '1.5rem' }}>
              All Checklists
            </Typography>
          }
          sx={{ 
            borderBottom: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'grey.50'
          }}
        />
        
        <CardContent sx={{ p: 0 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'primary.light' }}>
                <TableCell sx={{ fontSize: '1.1rem', fontWeight: 700, color: 'white', py: 3 }}>
                  Title
                </TableCell>
                <TableCell sx={{ fontSize: '1.1rem', fontWeight: 700, color: 'white', py: 3 }}>
                  Description
                </TableCell>
                <TableCell sx={{ fontSize: '1.1rem', fontWeight: 700, color: 'white', py: 3 }}>
                  Items Count
                </TableCell>
                <TableCell sx={{ fontSize: '1.1rem', fontWeight: 700, color: 'white', py: 3 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {items.map((item) => (
                <TableRow 
                  key={item._id}
                  sx={{ 
                    '&:hover': { 
                      backgroundColor: 'action.hover' 
                    },
                    transition: 'background-color 0.2s ease'
                  }}
                >
                  <TableCell sx={{ fontSize: '1.1rem', py: 2.5, fontWeight: 600 }}>
                    {item.title}
                  </TableCell>
                  <TableCell sx={{ fontSize: '1.1rem', py: 2.5 }}>
                    {item.description || '-'}
                  </TableCell>
                  <TableCell sx={{ fontSize: '1.1rem', py: 2.5 }}>
                    <Chip 
                      label={`${(item.items || []).length} items`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell sx={{ py: 2.5 }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Edit Checklist">
                        <IconButton 
                          onClick={() => handleEdit(item)}
                          sx={{ 
                            backgroundColor: 'primary.light',
                            color: 'white',
                            '&:hover': { backgroundColor: 'primary.main' }
                          }}
                        >
                          <EditIcon sx={{ fontSize: 24 }} />
                        </IconButton>
                      </Tooltip>
                      
                      <Tooltip title="Duplicate Checklist">
                        <IconButton 
                          onClick={() => duplicateItem(item)}
                          sx={{ 
                            backgroundColor: 'secondary.light',
                            color: 'white',
                            '&:hover': { backgroundColor: 'secondary.main' }
                          }}
                        >
                          <ContentCopyIcon sx={{ fontSize: 24 }} />
                        </IconButton>
                      </Tooltip>
                      
                      <Tooltip title="Delete Checklist">
                        <IconButton 
                          color="error"
                          onClick={() => setConfirmDelete({ open: true, id: item._id })}
                          sx={{ 
                            backgroundColor: 'error.light',
                            color: 'white',
                            '&:hover': { backgroundColor: 'error.main' }
                          }}
                        >
                          <DeleteIcon sx={{ fontSize: 24 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              
              {items.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                    <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
                      <ChecklistIcon sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
                      <Typography variant="h6" sx={{ fontSize: '1.3rem', mb: 1 }}>
                        No checklists found
                      </Typography>
                      <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
                        {search ? 'Try adjusting your search terms' : 'Create your first checklist to get started'}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>

        {/* Pagination */}
        <Box sx={{ 
          p: 3, 
          borderTop: '1px solid',
          borderColor: 'divider',
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2
        }}>
          <Button 
            variant="outlined"
            disabled={meta.page <= 1}
            onClick={() => load({ page: Math.max(1, meta.page - 1) })}
            sx={{ fontSize: '1.1rem', px: 3, py: 1 }}
          >
            Previous
          </Button>
          
          <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
            Page {meta.page} of {totalPages} • {meta.total} total items
          </Typography>
          
          <Button 
            variant="outlined"
            disabled={meta.page >= totalPages}
            onClick={() => load({ page: Math.min(totalPages, meta.page + 1) })}
            sx={{ fontSize: '1.1rem', px: 3, py: 1 }}
          >
            Next
          </Button>
        </Box>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog 
        open={open} 
        onClose={() => setOpen(false)} 
        fullWidth 
        maxWidth="lg"
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: 'primary.main',
          color: 'white',
          fontSize: '1.4rem',
          fontWeight: 600,
          py: 3
        }}>
          {editing ? 'Edit Checklist' : 'Create New Checklist'}
        </DialogTitle>
        
        <DialogContent sx={{ p: 4 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField 
                fullWidth 
                label="Checklist Title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                sx={{ 
                  mb: 2,
                  '& .MuiInputLabel-root': { fontSize: '1.1rem' },
                  '& .MuiInputBase-input': { fontSize: '1.2rem', py: 1.5 }
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField 
                fullWidth 
                label="Description"
                multiline
                rows={2}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                sx={{ 
                  '& .MuiInputLabel-root': { fontSize: '1.1rem' },
                  '& .MuiInputBase-input': { fontSize: '1.1rem' }
                }}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 4 }} />

          {/* Checklist Items Section */}
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ fontSize: '1.3rem', fontWeight: 600 }}>
                Checklist Items ({form.items.length})
              </Typography>
              <Button 
                onClick={addItemRow} 
                variant="contained"
                startIcon={<AddIcon />}
                sx={{ fontSize: '1.1rem', px: 3 }}
              >
                Add Item
              </Button>
            </Box>

            {form.items.length === 0 && (
              <Alert severity="info" sx={{ mb: 3, fontSize: '1.1rem' }}>
                No items added yet. Click "Add Item" to start building your checklist.
              </Alert>
            )}

            {(form.items || []).map((item, idx) => (
              <Paper 
                key={idx} 
                sx={{ 
                  p: 3, 
                  mb: 2, 
                  borderRadius: 2,
                  border: '2px solid',
                  borderColor: 'divider',
                  backgroundColor: 'grey.50'
                }}
              >
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={4}>
                    <TextField 
                      fullWidth 
                      label="Item Label"
                      value={item.label}
                      onChange={(e) => updateItemRow(idx, { label: e.target.value })}
                      sx={{ 
                        '& .MuiInputLabel-root': { fontSize: '1.1rem' },
                        '& .MuiInputBase-input': { fontSize: '1.1rem' }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={2}>
                    <TextField 
                      select 
                      fullWidth 
                      label="Input Type"
                      value={item.type}
                      onChange={(e) => updateItemRow(idx, { type: e.target.value })}
                      sx={{ 
                        '& .MuiInputLabel-root': { fontSize: '1.1rem' },
                        '& .MuiInputBase-input': { fontSize: '1.1rem' }
                      }}
                    >
                      <MenuItem value="boolean" sx={{ fontSize: '1.1rem' }}>Boolean</MenuItem>
                      <MenuItem value="text" sx={{ fontSize: '1.1rem' }}>Text</MenuItem>
                      <MenuItem value="number" sx={{ fontSize: '1.1rem' }}>Number</MenuItem>
                      <MenuItem value="select" sx={{ fontSize: '1.1rem' }}>Select</MenuItem>
                    </TextField>
                  </Grid>

                  <Grid item xs={12} md={2}>
                    <TextField 
                      label="Field Key"
                      value={item.key}
                      onChange={(e) => updateItemRow(idx, { key: e.target.value })}
                      fullWidth
                      sx={{ 
                        '& .MuiInputLabel-root': { fontSize: '1.1rem' },
                        '& .MuiInputBase-input': { fontSize: '1.1rem' }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={2}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={item.required}
                          onChange={(e) => updateItemRow(idx, { required: e.target.checked })}
                          sx={{ '& .MuiSvgIcon-root': { fontSize: 28 } }}
                        />
                      }
                      label={
                        <Typography sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                          Required
                        </Typography>
                      }
                    />
                  </Grid>

                  <Grid item xs={12} md={2}>
                    <IconButton 
                      color="error" 
                      onClick={() => removeItemRow(idx)}
                      sx={{ 
                        backgroundColor: 'error.light',
                        color: 'white',
                        '&:hover': { backgroundColor: 'error.main' }
                      }}
                    >
                      <DeleteIcon sx={{ fontSize: 24 }} />
                    </IconButton>
                  </Grid>

                  {item.type === 'select' && (
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Options (comma separated)"
                        value={(item.options || []).join(', ')}
                        onChange={(e) => updateItemRow(idx, { 
                          options: e.target.value.split(',').map(s => s.trim()).filter(Boolean) 
                        })}
                        helperText="Separate options with commas"
                        sx={{ 
                          '& .MuiInputLabel-root': { fontSize: '1.1rem' },
                          '& .MuiInputBase-input': { fontSize: '1.1rem' },
                          '& .MuiFormHelperText-root': { fontSize: '1rem' }
                        }}
                      />
                    </Grid>
                  )}
                </Grid>
              </Paper>
            ))}
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button 
            onClick={() => setOpen(false)}
            sx={{ fontSize: '1.1rem', px: 4, py: 1 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            variant="contained"
            sx={{ fontSize: '1.1rem', px: 4, py: 1 }}
          >
            Save Checklist
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={confirmDelete.open} 
        onClose={() => setConfirmDelete({ open: false, id: null })}
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: 'error.main',
          color: 'white',
          fontSize: '1.3rem',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <WarningIcon />
          Confirm Delete
        </DialogTitle>
        
        <DialogContent sx={{ p: 4 }}>
          <Typography variant="body1" sx={{ fontSize: '1.2rem', lineHeight: 1.6 }}>
            Are you sure you want to delete this checklist? This action cannot be undone and all associated data will be permanently removed.
          </Typography>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button 
            onClick={() => setConfirmDelete({ open: false, id: null })}
            sx={{ fontSize: '1.1rem', px: 4 }}
          >
            Cancel
          </Button>
          <Button 
            color="error" 
            variant="contained"
            onClick={() => handleDelete(confirmDelete.id)}
            sx={{ fontSize: '1.1rem', px: 4 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};